import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

import { listActionPlans, type ActionPlan } from "@/api/actionPlans";
import { listAssessments, type Assessment } from "@/api/assessments";
import { listIncidents, type Incident } from "@/api/incidents";
import { Btn2 } from "@/components/hifi/Btn2";
import { Card, CardHeader, TableHeader } from "@/components/hifi/Card";
import { Icon } from "@/components/hifi/Icon";
import { IconBtn } from "@/components/hifi/IconBtn";
import { KPICard } from "@/components/hifi/KPICard";
import { Pill } from "@/components/hifi/Pill";
import { RiskBadge, type RiskLevel } from "@/components/hifi/RiskBadge";
import { Avatar, Kbd, Label, SevDot } from "@/components/hifi/misc";
import { ShellLayout } from "@/components/shell/ShellLayout";
import { useAuthStore } from "@/stores/auth";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      assessments: Assessment[];
      actionPlans: ActionPlan[];
      incidents: Incident[];
    };

export function DashboardPage(): ReactNode {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [assessments, actionPlans, incidents] = await Promise.all([
          listAssessments({ limit: 50 }),
          listActionPlans({ limit: 50 }),
          listIncidents(),
        ]);
        if (!cancelled)
          setState({
            kind: "ready",
            assessments,
            actionPlans,
            incidents,
          });
      } catch (err) {
        if (!cancelled)
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Failed to load",
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const greet = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const firstName = user?.first_name ?? "there";
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <ShellLayout
      active="dashboard"
      topbar={{
        crumbs: ["My workspace", "Today"],
        title: `${greet}, ${firstName}`,
        subtitle: today,
        right: (
          <>
            <Btn2 variant="ghost" size="sm" icon="calendar">
              Today
            </Btn2>
            <Btn2 variant="ghost" size="sm" icon="download">
              Export
            </Btn2>
            <Btn2 variant="default" size="sm" icon="refresh" />
            <div className="mx-1 h-5 w-px bg-n-200" />
            <Btn2
              variant="primary"
              size="sm"
              icon="plus"
              onClick={() => void navigate({ to: "/assessments" })}
            >
              New assessment
            </Btn2>
          </>
        ),
      }}
    >
      {state.kind === "loading" && (
        <div className="p-8 text-center text-n-500">Loading dashboard…</div>
      )}
      {state.kind === "error" && (
        <div className="m-5 rounded-3 border border-bad-bg bg-bad-bg p-4 text-sm text-bad">
          {state.message}
        </div>
      )}
      {state.kind === "ready" && (
        <DashboardBody
          assessments={state.assessments}
          actionPlans={state.actionPlans}
          incidents={state.incidents}
        />
      )}
    </ShellLayout>
  );
}

function DashboardBody({
  assessments,
  actionPlans,
  incidents,
}: {
  assessments: Assessment[];
  actionPlans: ActionPlan[];
  incidents: Incident[];
}): ReactNode {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const openTasks = actionPlans.filter(
    (p) => p.status !== "COMPLETED" && p.status !== "CANCELLED",
  );
  const overdue = openTasks.filter(
    (p) => p.target_date && new Date(p.target_date) < new Date(),
  );
  const myTasks = openTasks
    .filter((p) => !p.responsible_person || p.responsible_person === user?.id)
    .slice(0, 8);

  const draftAssessments = assessments.filter(
    (a) => a.status === "DRAFT" || a.status === "IN_PROGRESS",
  );
  const awaitingReview = assessments.filter((a) => a.review_status === "PENDING");
  const openIncidents = incidents.filter(
    (i) => i.status !== "CLOSED" && i.status !== "RESOLVED",
  );
  const criticalIncidents = openIncidents.filter(
    (i) => i.severity === "CRITICAL" || i.severity === "HIGH",
  );

  const thisWeek = assessments.filter((a) => {
    if (!a.completed_at) return false;
    const d = new Date(a.completed_at);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return d >= weekAgo;
  }).length;

  const sla =
    openTasks.length === 0
      ? 100
      : Math.round(
          ((openTasks.length - overdue.length) / openTasks.length) * 100,
        );

  const sev = (p: ActionPlan): RiskLevel => {
    const pr = p.risk_priority?.toUpperCase() ?? "";
    if (pr.includes("EXTREME") || pr.includes("CRITICAL")) return "Extreme";
    if (pr.includes("HIGH")) return "High";
    if (pr.includes("MED") || pr.includes("MOD")) return "Moderate";
    if (pr.includes("LOW")) return "Low";
    return "Negligible";
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* KPI strip */}
      <div className="px-5 pt-3.5">
        <div
          className="grid gap-2.5"
          style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}
        >
          <KPICard
            label="My open tasks"
            value={openTasks.length}
            delta={overdue.length > 0 ? `${overdue.length} overdue` : "on track"}
            sub={`${overdue.length} overdue`}
            bad={overdue.length > 0}
          />
          <KPICard
            label="Assigned to me"
            value={myTasks.length}
            sub="action items"
          />
          <KPICard
            label="Needs my review"
            value={awaitingReview.length}
            sub={`${awaitingReview.length} assessments`}
            bad={awaitingReview.length > 0}
          />
          <KPICard
            label="This week"
            value={thisWeek}
            sub="completed assessments"
          />
          <KPICard
            label="My SLA · 30d"
            value={`${sla}%`}
            sub="target ≥ 90%"
            good={sla >= 90}
            bad={sla < 75}
          />
          <KPICard
            label="Open incidents"
            value={openIncidents.length}
            sub={`${criticalIncidents.length} critical/high`}
            bad={criticalIncidents.length > 0}
            context
          />
        </div>
      </div>

      {/* Main row: tasks + assessments + right rail */}
      <div
        className="grid min-h-0 flex-1 gap-3.5 px-5 pt-3.5 pb-3.5"
        style={{ gridTemplateColumns: "1.25fr 1.1fr 1fr" }}
      >
        {/* Tasks */}
        <Card className="relative flex min-h-0 flex-col">
          <CardHeader
            title="My tasks"
            pills={
              <>
                {overdue.length > 0 && (
                  <Pill variant="bad" icon="alert">
                    {overdue.length} overdue
                  </Pill>
                )}
                <Pill>{openTasks.length} open</Pill>
              </>
            }
            right={
              <>
                <Btn2 variant="ghost" size="xs" icon="filter">
                  Filter
                </Btn2>
                <Btn2 variant="ghost" size="xs" icon="sort">
                  Due
                </Btn2>
              </>
            }
          />
          <TableHeader cols="14px minmax(0,1fr) 90px 72px">
            <span />
            <span>Task</span>
            <span>Due</span>
            <span>Priority</span>
          </TableHeader>
          <div className="flex-1 overflow-auto">
            {myTasks.length === 0 && (
              <div className="p-6 text-center text-[12px] text-n-500">
                No open tasks assigned to you.
              </div>
            )}
            {myTasks.map((t) => {
              const isOverdue =
                t.target_date != null && new Date(t.target_date) < new Date();
              return (
                <div
                  key={t.id}
                  className="border-b border-n-100 px-3.5 py-2.5"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "14px minmax(0,1fr) 90px 72px",
                    gap: 10,
                    alignItems: "center",
                    background: isOverdue
                      ? "rgba(251, 229, 224, 0.25)"
                      : "transparent",
                  }}
                >
                  <div
                    className="rounded-[3px] border-[1.5px] bg-white"
                    style={{
                      width: 14,
                      height: 14,
                      borderColor: isOverdue
                        ? "var(--bad)"
                        : "var(--n-300)",
                    }}
                  />
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-medium leading-[1.3] tracking-[-0.1px] text-n-900">
                      {t.action_required}
                    </div>
                    <div className="mt-px flex items-center gap-1 truncate text-[11px] text-n-500">
                      <span
                        className={`font-mono font-medium ${isOverdue ? "text-bad" : "text-n-500"}`}
                      >
                        {t.id.slice(0, 8)}
                      </span>
                      <span className="text-n-300">·</span>
                      <Icon name="link" size={10} color="var(--n-400)" />
                      <span>
                        Assessment ·{" "}
                        <span className="font-mono text-a-600">
                          {t.assessment_id.slice(0, 8)}
                        </span>
                      </span>
                    </div>
                  </div>
                  <span
                    className={`truncate text-[11.5px] font-medium ${isOverdue ? "font-semibold text-bad" : "text-n-700"}`}
                  >
                    {t.target_date
                      ? new Date(t.target_date).toLocaleDateString()
                      : "—"}
                  </span>
                  <RiskBadge level={sev(t)} size="sm" />
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 border-t border-n-150 bg-n-50 px-3.5 py-2">
            <div className="flex items-center gap-1.5 text-[11px] text-n-500">
              <Kbd>↵</Kbd> open <Kbd>␣</Kbd> toggle <Kbd>G</Kbd> go to source
            </div>
            <div className="flex-1" />
            <Btn2
              variant="ghost"
              size="xs"
              iconRight="arrRight"
              onClick={() => void navigate({ to: "/action-plans" })}
            >
              View all
            </Btn2>
          </div>
        </Card>

        {/* Assessments in flight */}
        <Card className="relative flex min-h-0 flex-col">
          <CardHeader
            title="Assessments in flight"
            pills={
              <Pill variant="accent" icon="list">
                {draftAssessments.length} active
              </Pill>
            }
            right={
              <>
                <Btn2 variant="ghost" size="xs">
                  Agenda
                </Btn2>
                <Btn2
                  variant="ghost"
                  size="xs"
                  icon="plus"
                  onClick={() => void navigate({ to: "/assessments" })}
                />
              </>
            }
          />
          <div className="flex-1 overflow-auto">
            {draftAssessments.length === 0 && (
              <div className="p-6 text-center text-[12px] text-n-500">
                No active assessments.
              </div>
            )}
            {draftAssessments.slice(0, 10).map((a) => (
              <div
                key={a.id}
                className="cursor-pointer border-b border-n-100 px-3.5 py-2.5 hover:bg-n-50"
                onClick={() =>
                  void navigate({
                    to: "/assessments/$assessmentId/wizard",
                    params: { assessmentId: a.id },
                  })
                }
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-[12.5px] font-medium text-n-900">
                    {a.title}
                  </span>
                  <Pill
                    variant={a.status === "IN_PROGRESS" ? "accent" : "default"}
                  >
                    {a.status.toLowerCase().replace("_", " ")}
                  </Pill>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-n-500">
                  <span className="font-mono text-n-500">
                    {a.id.slice(0, 8)}
                  </span>
                  <span className="text-n-300">·</span>
                  <span>Step {a.current_step} / 7</span>
                  <span className="text-n-300">·</span>
                  <span>
                    {a.compliance_tags.length > 0
                      ? a.compliance_tags[0]
                      : a.assessment_type}
                  </span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-n-100">
                  <div
                    className="h-full bg-a-500"
                    style={{ width: `${Math.round((a.current_step / 7) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 border-t border-n-150 bg-n-50 px-3.5 py-2">
            <Icon name="refresh" size={11} color="var(--n-400)" />
            <span className="text-[11px] text-n-500">
              {assessments.length} total · auto-saved
            </span>
            <div className="flex-1" />
            <Btn2
              variant="ghost"
              size="xs"
              iconRight="arrRight"
              onClick={() => void navigate({ to: "/assessments" })}
            >
              Open all
            </Btn2>
          </div>
        </Card>

        {/* Right rail */}
        <div className="flex min-h-0 flex-col gap-3.5">
          <Card accent="var(--bad)" className="relative flex flex-1 flex-col">
            <CardHeader
              title="Pending incident reviews"
              icon={<Icon name="alert" size={14} color="var(--bad)" />}
              pills={
                <Pill variant="bad">{openIncidents.length}</Pill>
              }
              right={
                <Btn2
                  variant="ghost"
                  size="xs"
                  iconRight="arrRight"
                  onClick={() => void navigate({ to: "/incidents" })}
                >
                  Queue
                </Btn2>
              }
            />
            <div className="flex-1 overflow-auto">
              {openIncidents.length === 0 && (
                <div className="p-6 text-center text-[12px] text-n-500">
                  No open incidents.
                </div>
              )}
              {openIncidents.slice(0, 5).map((inc) => (
                <div
                  key={inc.id}
                  className="border-b border-n-100 px-3.5 py-2.5"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "12px 1fr auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <SevDot sev={inc.severity.toLowerCase()} />
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-[10px] font-semibold text-a-600">
                        {inc.id.slice(0, 8)}
                      </span>
                      <span className="truncate text-[12.5px] font-medium text-n-900">
                        {inc.title}
                      </span>
                    </div>
                    <div className="mt-px truncate text-[11px] text-n-500">
                      {inc.occurred_at
                        ? new Date(inc.occurred_at).toLocaleDateString()
                        : "—"}{" "}
                      · {inc.severity.toLowerCase()}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <IconBtn name="check" size="sm" label="Acknowledge" />
                    <IconBtn
                      name="arrRight"
                      size="sm"
                      label="Open"
                      onClick={() => void navigate({ to: "/incidents" })}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card accent="var(--a-500)" className="relative flex flex-1 flex-col">
            <CardHeader
              title="Awaiting review"
              icon={<Icon name="checkBox" size={14} color="var(--a-600)" />}
              pills={<Pill variant="accent">{awaitingReview.length}</Pill>}
            />
            <div className="flex-1 overflow-auto">
              {awaitingReview.length === 0 && (
                <div className="p-6 text-center text-[12px] text-n-500">
                  Nothing awaiting review.
                </div>
              )}
              {awaitingReview.slice(0, 4).map((a) => (
                <div
                  key={a.id}
                  className="border-b border-n-100 px-3.5 py-2.5"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) 82px",
                    columnGap: 10,
                  }}
                >
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-[10px] font-semibold text-a-600">
                        {a.id.slice(0, 8)}
                      </span>
                      <span className="truncate text-[12.5px] font-medium text-n-900">
                        {a.title}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Avatar
                        initials={(a.title[0] ?? "A").toUpperCase()}
                        size={20}
                        color="#fff"
                        bg="var(--n-700)"
                      />
                      <span className="truncate text-[11px] text-n-500">
                        Step {a.current_step}/7 · {a.assessment_type}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-stretch gap-1.5">
                    <Btn2
                      variant="default"
                      size="xs"
                      iconRight="arrRight"
                      full
                      onClick={() =>
                        void navigate({
                          to: "/assessments/$assessmentId/wizard",
                          params: { assessmentId: a.id },
                        })
                      }
                    >
                      Review
                    </Btn2>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Bottom strip */}
      <div
        className="grid gap-2.5 px-5 pb-3"
        style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
      >
        <Card
          className="flex items-center gap-2.5"
          style={{ padding: "7px 12px" }}
        >
          <div className="flex-shrink-0">
            <Label>Org risk heat</Label>
            <div className="mt-px text-[10.5px] text-n-600">
              {assessments.length} assessments · last 30d
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex gap-0.5">
            {[
              { bg: "var(--r-neg)", n: "Neg" },
              { bg: "var(--r-low)", n: "Low" },
              { bg: "var(--r-mod)", n: "Mod" },
              { bg: "var(--r-high)", n: "High" },
              { bg: "var(--r-ext)", n: "Ext" },
            ].map((c) => (
              <div
                key={c.n}
                className="flex h-4 w-8 items-center justify-center rounded-1 border border-n-200 font-mono text-[9px] font-semibold text-n-700"
                style={{ background: c.bg }}
              >
                {c.n}
              </div>
            ))}
          </div>
        </Card>
        <Card
          className="flex items-center gap-2.5"
          style={{ padding: "7px 12px" }}
        >
          <div className="flex-shrink-0">
            <Label>My task SLA</Label>
            <div className="mt-px text-[10.5px] text-n-600">
              {openTasks.length} open · {overdue.length} overdue
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="flex h-1.5 overflow-hidden rounded-1 border border-n-150">
              <div
                className="bg-risk-ext"
                style={{
                  width: `${openTasks.length ? (overdue.length / openTasks.length) * 100 : 0}%`,
                }}
                title="overdue"
              />
              <div className="flex-1 bg-risk-low" title="on track" />
            </div>
            <div className="mt-0.5 flex justify-between font-mono text-[9.5px] text-n-500">
              <span>{overdue.length} overdue</span>
              <span>{openTasks.length - overdue.length} on track</span>
            </div>
          </div>
        </Card>
        <Card
          className="flex items-center gap-2.5"
          style={{ padding: "7px 12px" }}
        >
          <div className="flex-shrink-0">
            <Label>Compliance coverage</Label>
            <div className="mt-px text-[10.5px] text-n-600">
              active frameworks
            </div>
          </div>
          <div className="flex-1" />
          <div className="flex flex-wrap justify-end gap-0.5">
            <Pill variant="accent">ISO 31000</Pill>
            <Pill variant="accent">NIS2</Pill>
            <Pill variant="warn">CER</Pill>
          </div>
        </Card>
      </div>
    </div>
  );
}
